import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const SchoolBrandingSection = ({ teacher, onUpdate }) => {
  const [schoolName, setSchoolName] = useState(teacher?.school || '');
  const [logoPreview, setLogoPreview] = useState(teacher?.logo_url || null);
  const [logoFile, setLogoFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowed?.includes(file?.type)) {
      setMsg({ type: 'error', text: 'Please upload a JPG, PNG, SVG, or WebP image.' });
      return;
    }
    if (file?.size > 2 * 1024 * 1024) {
      setMsg({ type: 'error', text: 'File size must be under 2MB.' });
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e?.target?.result);
    reader?.readAsDataURL(file);
    setMsg(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      let logoUrl = teacher?.logo_url || null;
      if (logoFile) {
        const ext = logoFile?.name?.split('.')?.pop();
        const path = `school-logos/${teacher?.id}.${ext}`;
        const { error: uploadError } = await supabase?.storage
          ?.from('teacher-assets')
          ?.upload(path, logoFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase?.storage?.from('teacher-assets')?.getPublicUrl(path);
        logoUrl = urlData?.publicUrl;
      }
      const { error } = await supabase
        ?.from('teachers')
        ?.update({ school: schoolName?.trim(), logo_url: logoUrl, updated_at: new Date()?.toISOString() })
        ?.eq('id', teacher?.id);
      if (error) throw error;
      setMsg({ type: 'success', text: 'School branding saved successfully.' });
      onUpdate?.({ school: schoolName?.trim(), logo_url: logoUrl });
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to save branding.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
          <Icon name="Building2" size={20} className="text-secondary-foreground" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">School &amp; Branding</h2>
          <p className="text-sm text-muted-foreground">Customize how your school appears on QR codes and credential cards</p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">School Name</label>
          <input
            type="text"
            value={schoolName}
            onChange={e => setSchoolName(e?.target?.value)}
            placeholder="e.g. Riverside Elementary School"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
          <p className="text-xs text-muted-foreground mt-1">This name appears on printed QR codes and student credential cards.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">School Logo</label>
          <div
            onDragOver={e => { e?.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/svg+xml,image/webp"
              className="hidden"
              onChange={e => handleFile(e?.target?.files?.[0])}
            />
            {logoPreview ? (
              <div className="flex flex-col items-center gap-3">
                <img src={logoPreview} alt="School logo preview" className="h-20 w-auto object-contain rounded-lg" />
                <p className="text-xs text-muted-foreground">Click or drag to replace</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="Upload" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">Drag &amp; drop or click to upload</p>
                <p className="text-xs text-muted-foreground">JPG, PNG, SVG, WebP — max 2MB</p>
              </div>
            )}
          </div>
          {logoPreview && (
            <button
              onClick={e => { e?.stopPropagation(); setLogoPreview(null); setLogoFile(null); }}
              className="mt-2 text-xs text-error hover:underline flex items-center gap-1"
            >
              <Icon name="Trash2" size={12} /> Remove logo
            </button>
          )}
        </div>

        {msg && (
          <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
            msg?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
          }`}>
            <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
            {msg?.text}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Save" size={16} />}
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </div>
    </div>
  );
};

export default SchoolBrandingSection;
